import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChartConfig } from '../chart';
import { ChartHelper } from '../chart-helper';

export type Arc = { path: string, color: string, value: number, valueText: string, percentage: number | string, label: string };

@Component({
  standalone: false,
  selector: '[cs-pie-chart]',
  templateUrl: './pie-chart.component.html'


})
export class CsPieChartComponent implements OnInit, OnChanges {

  @Input('config') config: ChartConfig;
  private data: { label: string, value: number, startAngle?: number, endAngle?: number }[];
  hoveredIndex = -1;
  private pieCenterFactor; // = this.config.showLegend ? 0.7 : 1;

  private chartStartAngle = 0; // -Math.PI * 0.5;              // Start the chart at 12 o'clock instead of 3 o'clock
  private maxPullOutDistance = 7;                      // How far, in pixels, to pull slices out when hover
  private pullOutFrameStep = 0.5;                         // How many pixels to move a slice with each animation frame
  private pullOutFrameInterval = 60;                    // How long (in ms) between each animation frame
  private pullOutLabelPadding = 15;                     // Padding between pulled-out slice and its label
  private pullOutLabelFont = 12; // this.getFont(0.8, true);  // Pull-out slice label font
  private pullOutValueFont = 11; // this.getFont(0.7, false);  // Pull-out slice value font
  private pullOutValuePrefix = ' ';                     // Pull-out slice value prefix
  private pullOutShadowColour = 'rgba( 0, 0, 0, .5 )';  // Colour to use for the pull-out slice shadow
  private pullOutShadowOffsetX = 5;                     // X-offset (in pixels) of the pull-out slice shadow
  private pullOutShadowOffsetY = 5;                     // Y-offset (in pixels) of the pull-out slice shadow
  private pullOutShadowBlur = 5;                        // How much to blur the pull-out slice shadow
  private pullOutBorderWidth = 2;                       // Width (in pixels) of the pull-out slice border
  private pullOutBorderStyle = '#333';                  // Colour of the pull-out slice border
  private currentPullOutSlice = -1;     // The slice currently pulled out (-1 = no slice)
  private currentPullOutDistance = 0;   // How many pixels the pulled-out slice is currently pulled out in the animation
  private sliceBorderWidth = 1;                         // Width (in pixels) of the border around each slice
  private sliceBorderStyle = '#ffffff';                    // Colour of the border around each slice
  private sliceGradientColour = '#ddd';                 // Colour to use for one end of the chart gradient
  private hoveredAnimationSubscribtion: Subscription;              // Tracks the interval ID for the animation created when hovered
  private animationSubscribtion: Subscription;              // Tracks the interval ID for the animation while drawing
  private animated = true; // = !this.config.animateIntro;
  private currentAnimationStep = 1; // this.config.animateIntro ? 0 : 1;
  private animationStep = 0.05;
  private animatioFrameInterval = 40;

  center: { X: number, Y: number };
  radius: number = 0;
  private totalValue: number;
  private legendItemHeight;
  arcs: Arc[];

  constructor() {
  }

  private isDataReady() {
    return this.config && this.config.data && this.config.data.datasets && this.config.data.datasets.length;
  }

  ngOnInit() {
    setTimeout(() => {
      if (this.isDataReady()) {
        this.initData();
        this.calculate();
      }
    }, 1);

  }

  ngOnChanges(changes: any) {
    if (changes.hasOwnProperty('config')) {
      if (this.isDataReady()) {
        this.initData();
        this.calculate();
      }
    }
  }

  private initData() {
    this.pieCenterFactor = this.config.showLegend ? 0.7 : 1;
    this.animated = !this.config.animateIntro;
    this.currentAnimationStep = this.config.animateIntro ? 0 : 1;
    let currentPos = 0; // The current position of the slice in the pie (from 0 to 1)
    this.data = this.config.data.labels.map((l, i) => {
      return { label: l, value: this.config.data.datasets[0].data[i] };
    });
    // A pie/donut is a share of a POSITIVE whole — a wedge has no negative angle. So the
    // geometry (and the % denominator) is the sum of positive values only; non-positive
    // entries (e.g. a Discounts offset) still appear in the legend but draw no wedge, instead
    // of winding the arc backwards and overlapping the other slices.
    this.totalValue = this.data.reduce((sum, d) => sum + Math.max(0, +d.value), 0);
    const span = this.totalValue > 0 ? this.totalValue : 1;
    for (let i = 0; i < this.data.length; i++) {
      const fraction = Math.max(0, +this.data[i].value) / span;
      this.data[i].startAngle = 2 * Math.PI * currentPos;
      this.data[i].endAngle = 2 * Math.PI * (currentPos + fraction);
      currentPos += fraction;
    }
  }

  private calculate() {
    if (!this.config.chartWidth || !this.config.chartHeight) {
      return;
    }
    const minRadius = 80;
    this.center = {
      X: this.config.chartWidth * this.pieCenterFactor / 2 / 1.05,
      Y: this.config.chartHeight / 2 / 1.05
    };
    this.radius = Math.min(this.config.chartHeight / 1.1, this.config.chartWidth * this.pieCenterFactor / 1.1) / 2;
    this.radius = Math.max(this.radius, minRadius);
    this.arcs = [];
    for (let i = 0; i < this.data.length; i++) {
      let slice = this.data[i];
      // Compute the adjusted start and end angles for the slice
      let startAngle = (slice.startAngle + this.chartStartAngle);
      let endAngle = (slice.endAngle + this.chartStartAngle);
      let end = {
        x: this.center.X + this.radius * Math.cos(startAngle),
        y: this.center.Y + this.radius * Math.sin(startAngle)
      };
      let start = {
        x: this.center.X + this.radius * Math.cos(endAngle),
        y: this.center.Y + this.radius * Math.sin(endAngle)
      };
      let arcSweep = endAngle - startAngle <= Math.PI ? '0' : '1';
      // Non-positive slices contribute no wedge (zero-width arc) — they stay in the legend only.
      let path = +this.data[i].value > 0
        ? [
            'M', start.x, start.y,
            'A', this.radius, this.radius, 0, arcSweep, 0, end.x, end.y,
            'L', this.center.X, this.center.Y,
            'Z'].join(' ')
        : '';
      const span = this.totalValue > 0 ? this.totalValue : 1;
      this.arcs.push({
        path,
        color: this.config.colors[i % this.config.colors.length],
        label: this.config.data.labels[i],
        value: this.data[i].value,
        valueText: ChartHelper.getValueLocale(this.data[i].value),
        percentage: new Intl.NumberFormat(ChartHelper.locale,
          { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(100 * this.data[i].value / span)
      });

    }
  }
}
